import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { updateUserProfile } from '@/services/api';
import toast from 'react-hot-toast';
import PhoneInput from 'react-country-phone-input';
import { X, Phone, Globe } from 'lucide-react';
import 'react-country-phone-input/lib/style.css';
import { trackOnboardingPhoneCompleted, trackOnboardingPhoneSkipped } from '@/utils/eventTracking';

// Custom styles for the phone input to match dark theme
const phoneInputStyles = `
  .react-country-phone-input .react-country-phone-input__input {
    background-color: #374151 !important;
    border-color: #4B5563 !important;
    color: #F3F4F6 !important;
  }
  .react-country-phone-input .react-country-phone-input__input:focus {
    border-color: #8B5CF6 !important;
    box-shadow: 0 0 0 1px #8B5CF6 !important;
  }
  .react-country-phone-input .react-country-phone-input__input::placeholder {
    color: #9CA3AF !important;
  }
  .react-country-phone-input .react-country-phone-input__button {
    background-color: #374151 !important;
    border-color: #4B5563 !important;
    color: #F3F4F6 !important;
  }
  .react-country-phone-input .react-country-phone-input__button:hover {
    background-color: #4B5563 !important;
  }
  .react-country-phone-input .react-country-phone-input__dropdown {
    background-color: #374151 !important;
    border-color: #4B5563 !important;
  }
  .react-country-phone-input .react-country-phone-input__dropdown-item {
    color: #F3F4F6 !important;
  }
  .react-country-phone-input .react-country-phone-input__dropdown-item:hover {
    background-color: #4B5563 !important;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = phoneInputStyles;
  document.head.appendChild(styleSheet);
}


interface PhoneNumberModalProps {
    onClose: () => void;
}

const PhoneNumberModal = ({ onClose }: PhoneNumberModalProps) => {
    const [phoneNumber, setPhoneNumber] = useState('');
    const [countryCode, setCountryCode] = useState('');
    const [countryName, setCountryName] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [defaultCountry, setDefaultCountry] = useState('us');
    const [isDetectingCountry, setIsDetectingCountry] = useState(true);

    const handleClose = () => {
        // Track phone prompt skipped
        trackOnboardingPhoneSkipped();
        onClose();
    };

    // IP-based country detection
    useEffect(() => {
        const detectCountry = async () => {
            try {
                const response = await fetch('https://ipapi.co/json/');
                const data = await response.json();
                if (data.country_code) {
                    setDefaultCountry(data.country_code.toLowerCase());
                }
            } catch (error) {
                console.log('Country detection failed, using default (US)');
            } finally {
                setIsDetectingCountry(false);
            }
        };

        detectCountry();
    }, []);

    const handlePhoneChange = (value: string, data: any) => {
        setPhoneNumber(value.slice(data.dialCode.length));
        setCountryCode(data.dialCode);
        setCountryName(data.name);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!phoneNumber.trim() || !countryCode) {
            toast.error('Please enter a valid phone number');
            return;
        }

        try {
            setIsSubmitting(true);
            await updateUserProfile({
                mobile : phoneNumber.trim(),
                country_code: countryCode,
                country_name: countryName
            });
            localStorage.setItem('country_code', countryCode);
            localStorage.setItem('mobile', phoneNumber.trim());
            
            // Track successful phone completion
            trackOnboardingPhoneCompleted(countryName);
            
            toast.success('Phone number updated successfully');
            onClose();
        } catch (error) {
            console.error('Failed to update phone number:', error);
            toast.error('Failed to update phone number. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50 p-4">
            <Card className="bg-slate-800 border-gray-600 shadow-xl max-w-md w-full">
                <CardHeader className="relative">
                    <button
                        onClick={handleClose}
                        className="absolute top-4 bg-transparent right-4 text-gray-400 hover:text-white transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                    <div className="flex items-center space-x-3 mb-2">
                        <div className="p-2 bg-purple-500/20 rounded-full">
                            <Phone className="w-5 h-5 text-purple-400" />
                        </div>
                        <CardTitle className="text-xl font-semibold text-white">
                            Complete Your Profile
                        </CardTitle>
                    </div>
                    <CardDescription className="text-gray-300">
                        Add your phone number to complete your profile setup and enhance your experience.
                    </CardDescription>
                </CardHeader>

                <CardContent>
                    <form onSubmit={handleSubmit}>
                        <div className="space-y-6">
                            <div className="space-y-2">
                                <Label htmlFor="phone-number" className="text-white font-medium">
                                    Phone Number
                                </Label>
                                <div className="relative">
                                    {isDetectingCountry && (
                                        <div className="absolute inset-0 flex items-center justify-center bg-slate-700/50 rounded-lg z-10">
                                            <div className="flex items-center space-x-2 text-gray-300">
                                                <Globe className="w-4 h-4 animate-spin" />
                                                <span className="text-sm">Detecting your country...</span>
                                            </div>
                                        </div>
                                    )}
                                    <PhoneInput
                                        onChange={handlePhoneChange}
                                        country={defaultCountry}
                                        autoFormat={false}
                                        inputProps={{
                                            id: 'phone-number',
                                            required: true,
                                            className: 'w-full pl-12 pr-3 py-2 bg-slate-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none'
                                        }}
                                        buttonStyle={{
                                            backgroundColor: '#374151',
                                            borderColor: '#4B5563',
                                            color: '#F3F4F6'
                                        }}
                                        dropdownStyle={{
                                            backgroundColor: '#374151',
                                            borderColor: '#4B5563',
                                            color:'#6b6b6b'
                                        }}
                                    />
                                </div>
                            </div>

                            <div className="flex justify-end space-x-3 pt-4">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={handleClose}
                                    disabled={isSubmitting}
                                    className="border-gray-600 text-black-300 hover:text-white hover:bg-gray-700 hover:border-gray-500"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={isSubmitting || isDetectingCountry}
                                    className="bg-purple-600 hover:bg-purple-700 text-white"
                                >
                                    {isSubmitting ? 'Updating...' : 'Update Profile'}
                                </Button>
                            </div>
                        </div>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
};

export default PhoneNumberModal; 